import React, { useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { Radar } from 'react-chartjs-2';
import type { RadarChartProps } from '../../types';

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

// Excel 蓝色系风格（仿参考图）：浅蓝填充 + 中蓝边线 + 深蓝数据点
const THEME = {
  line:       'rgba(91, 155, 213, 1)',        // 中蓝轮廓（Excel 经典 #5b9bd5）
  fill:       'rgba(143, 170, 220, 0.35)',    // 浅蓝填充（半透明）
  point:      'rgba(68, 114, 196, 1)',        // 深蓝数据点 #4472c4
  accent:     'rgba(255, 138, 61, 1)',        // 火山橙（TOP3 焦点保留）
  grid:       'rgba(200, 215, 240, 0.15)',    // 圆形网格：淡蓝灰
  labelBase:  'rgba(255, 255, 255, 0.88)',
  labelTop:   'rgba(255, 138, 61, 1)',
  tick:       'rgba(200, 215, 240, 0.45)',    // 刻度数字：淡蓝灰
};

// top3 阈值：得分排在前3的用橙色高亮
const getTopIndices = (values: number[], topN = 3): Set<number> => {
  const sorted = values
    .map((v, i) => ({ v, i }))
    .sort((a, b) => b.v - a.v)
    .slice(0, topN);
  return new Set(sorted.map((x) => x.i));
};

export const RadarChart: React.FC<RadarChartProps> = ({ title, data }) => {
  const [activeIdx, setActiveIdx] = useState<number | null>(null);

  const topSet = useMemo(() => getTopIndices(data.values, 3), [data.values]);

  // 标签："云计算相关：90"（冒号连接，与 Excel 参考图一致）
  const labels = useMemo(
    () => data.dimensions.map((d, i) => `${d.replace(/\d+$/, '')}：${data.values[i]}`),
    [data.dimensions, data.values]
  );

  // 每个点的颜色 & 尺寸：top3 用橙色大点，其余用深蓝小点
  const pointColors = useMemo(
    () => data.values.map((_, i) => (topSet.has(i) ? THEME.accent : THEME.point)),
    [data.values, topSet]
  );

  const pointRadii = useMemo(
    () => data.values.map((_, i) => (topSet.has(i) ? 6 : 3)),
    [data.values, topSet]
  );

  // pointLabels 颜色：top3 用橙色（醒目），普通项用亮白
  const pointLabelColors = useMemo(
    () => data.values.map((_, i) => (topSet.has(i) ? THEME.labelTop : THEME.labelBase)),
    [data.values, topSet]
  );

  const chartData = useMemo(() => ({
    labels,
    datasets: [
      {
        label: title,
        data: data.values,
        backgroundColor: THEME.fill,
        borderColor: THEME.line,
        borderWidth: 1.8,
        pointBackgroundColor: pointColors,
        pointBorderColor: '#0a192f',
        pointBorderWidth: 1.5,
        pointRadius: pointRadii,
        pointHoverRadius: 9,
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: THEME.accent,
        pointHoverBorderWidth: 3,
      },
    ],
  }), [labels, data.values, title, pointColors, pointRadii]);

  // 技术图恢复"棱角版"（多边形网格 + 压缩 min 放大差异），综合图保持"圆润版"
  const isTechnical = title.includes('技术');

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 1400,
      easing: 'easeOutCubic' as const,
    },
    interaction: {
      mode: 'nearest' as const,
      intersect: false,
    },
    onHover: (_evt: any, activeElements: any[]) => {
      setActiveIdx(activeElements[0]?.index ?? null);
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(5, 13, 28, 0.95)',
        borderColor: THEME.line,
        borderWidth: 1,
        titleColor: '#fff',
        titleFont: { size: 13, weight: 'bold' as const },
        bodyColor: THEME.accent,
        bodyFont: { size: 15, weight: 'bold' as const },
        padding: 12,
        displayColors: false,
        cornerRadius: 8,
        callbacks: {
          title: (items: any[]) => {
            const raw = items[0]?.label ?? '';
            // 去掉尾部的分数（含全角/半角冒号），保留维度名
            return String(raw).replace(/[：:]\s*\d+$/, '');
          },
          label: (context: any) =>
            `评分 ${context.raw} / 100${topSet.has(context.dataIndex) ? '  · TOP' : ''}`,
        },
      },
    },
    scales: {
      r: {
        // 综合图：min=0 圆润；技术图：min=45 放大差异出棱角
        beginAtZero: !isTechnical,
        angleLines: { color: THEME.grid, lineWidth: 1 },
        // 综合图圆形网格，技术图多边形网格（棱角感来源）
        grid: { color: THEME.grid, lineWidth: 1, circular: !isTechnical },
        pointLabels: {
          font: {
            size: 13,
            family: 'system-ui, -apple-system, "PingFang SC", sans-serif',
            weight: 600 as const,
          },
          color: (ctx: any) => pointLabelColors[ctx.index] ?? THEME.labelBase,
          padding: 12,
        },
        ticks: {
          // 综合图显示 10-100 刻度；技术图隐藏刻度让棱角更纯粹
          backdropColor: 'transparent',
          color: THEME.tick,
          font: { size: 10, weight: 500 as const },
          stepSize: 20,
          showLabelBackdrop: false,
          display: !isTechnical,
          z: 1,
        },
        min: isTechnical ? 45 : 0,
        max: isTechnical ? 96 : 100,
      },
    },
    elements: {
      // 综合图 tension 大呈圆弧；技术图 tension=0 呈直折线（尖角）
      line: {
        tension: isTechnical ? 0 : 0.25,
        borderJoinStyle: 'round' as const,
      },
    },
    aspectRatio: 1,
  }), [pointLabelColors, topSet, isTechnical]);

  return (
    <div className="radar-wrap" data-testid="radar-chart-container"
         onMouseLeave={() => setActiveIdx(null)}>
      {/* 图例说明：top3 用橙 */}
      <div className="radar-legend-note">
        <span className="radar-legend-dot radar-legend-dot--top"></span>
        <span>TOP 3 优势项</span>
        <span className="radar-legend-sep"></span>
        <span className="radar-legend-dot radar-legend-dot--base"></span>
        <span>能力雷达</span>
        {activeIdx !== null && (
          <span className="radar-legend-active">
            <i className="fas fa-crosshairs"></i>
            {data.dimensions[activeIdx]} · {data.values[activeIdx]} 分
          </span>
        )}
      </div>

      {/* 雷达图本体 */}
      <div className="radar-body" data-testid="radar-chart">
        <Radar data={chartData} options={options} data-testid="radar-chart-canvas" />
      </div>

      {/* 隐藏 test hooks */}
      <div className="hidden" data-testid={title.includes('综合') ? 'comprehensive-dimensions' : 'technical-dimensions'}>
        {data.dimensions.join(',')}
      </div>
      <div className="hidden" data-testid="radar-data-point">
        {data.values.map((v, i) => <span key={i} data-testid="radar-data-point">{v}</span>)}
      </div>
      <div className="hidden" data-testid="radar-legend">{title}</div>
    </div>
  );
};
